export interface WebSocketMessage {
    action: string;
    data?: any;
}

export interface ChatMessage {
    id: string;
    text: string;
    thinking?: string; // Optional thinking content extracted from <thinking> tags
    sender: "user" | "ai";
    timestamp: Date;
}

export class WebSocketService {
    private ws: WebSocket | null = null;
    private url: string;
    private jwtToken?: string;
    private onMessageCallback?: (message: ChatMessage) => void;
    private onConnectCallback?: () => void;
    private onDisconnectCallback?: () => void;
    private onErrorCallback?: (error: Event) => void;
    private reconnectAttempts = 0;
    private maxReconnectAttempts = 3;
    private reconnectDelay = 1000;
    private currentMessages: Map<string, string> = new Map();

    constructor(url: string, jwtToken?: string) {
        this.url = url;
        this.jwtToken = jwtToken;
    }

    connect(): Promise<void> {
        return new Promise((resolve, reject) => {
            try {
                // Append JWT token as query parameter if provided
                const wsUrl = this.jwtToken ? `${this.url}?token=${this.jwtToken}` : this.url;
                console.log("🔌 Connecting to WebSocket:", wsUrl);
                console.log("🔑 JWT Token:", this.jwtToken ? "Present" : "Not provided");
                this.ws = new WebSocket(wsUrl);

                this.ws.onopen = () => {
                    console.log("✅ WebSocket connected successfully");
                    this.reconnectAttempts = 0;
                    this.onConnectCallback?.();
                    resolve();
                };

                this.ws.onmessage = this.handleWebSocketMessage;

                this.ws.onclose = event => {
                    console.log("🔌 WebSocket closed:", event.code, event.reason);
                    this.onDisconnectCallback?.();

                    // Attempt to reconnect if not a clean close
                    if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
                        this.attemptReconnect();
                    }
                };

                this.ws.onerror = error => {
                    console.error("❌ WebSocket error:", error);
                    this.onErrorCallback?.(error);
                    reject(error);
                };
            } catch (error) {
                console.error("❌ Error creating WebSocket:", error);
                reject(error);
            }
        });
    }

    private parseSSEText(text: string): string {
        // Extract content from SSE format: data: "content"\n\n
        const lines = text.split("\n\n");
        let cleanedText = "";

        for (const line of lines) {
            if (line.startsWith("data: ")) {
                try {
                    // Remove 'data: ' prefix and parse JSON string
                    const content = line.substring(6);
                    const parsed = JSON.parse(content);
                    cleanedText += parsed;
                } catch {
                    // If not valid JSON, just remove 'data: ' prefix
                    cleanedText += line.substring(6);
                }
            }
        }

        // Filter out tool execution messages
        if (cleanedText.includes("[✅ Tool execution completed]")) {
            cleanedText = cleanedText.replace(/\[✅ Tool execution completed\]/g, "").trim();
        }

        return cleanedText;
    }

    private parseThinkingContent(text: string): { displayText: string; thinking?: string } {
        // Handle both complete and incomplete thinking tags

        // Check for complete thinking tags first
        const completeThinkingRegex = /<thinking>([\s\S]*?)<\/thinking>/g;
        const completeMatches = text.match(completeThinkingRegex);

        if (completeMatches && completeMatches.length > 0) {
            // Extract all complete thinking content
            let thinkingContent = "";
            completeMatches.forEach(match => {
                const content = match.replace(/<\/?thinking>/g, "").trim();
                thinkingContent += content + "\n\n";
            });

            // Remove complete thinking tags from display text
            const displayText = text.replace(completeThinkingRegex, "").trim();

            return {
                displayText,
                thinking: thinkingContent.trim(),
            };
        }

        // Check for incomplete thinking tag (streaming in progress)
        const incompleteThinkingMatch = text.match(/<thinking>([\s\S]*?)$/);

        if (incompleteThinkingMatch) {
            // Extract partial thinking content (everything after <thinking>)
            const thinkingContent = incompleteThinkingMatch[1].trim();

            // Remove the incomplete thinking section from display text
            const displayText = text.replace(/<thinking>[\s\S]*$/, "").trim();

            return {
                displayText,
                thinking: thinkingContent,
            };
        }

        // No thinking tags found
        return { displayText: text };
    }

    private handleWebSocketMessage = (event: MessageEvent): void => {
        console.log("📨 Received WebSocket message:", event.data);
        try {
            const data = JSON.parse(event.data);
            console.log("📨 Parsed message:", data);

            if (data.route === "converse") {
                const messageId = data.messageId;

                if (data.event === "start") {
                    // Initialize new message
                    console.log("📨 AI response started, messageId:", messageId);
                    this.currentMessages.set(messageId, "");
                } else if (data.event === "delta") {
                    // Accumulate text for this specific message from each delta event
                    const currentText = this.currentMessages.get(messageId) || "";
                    const updatedText = currentText + data.text;
                    this.currentMessages.set(messageId, updatedText);

                    // STEP 1: Parse SSE format first to get clean text
                    const cleanedText = this.parseSSEText(updatedText);

                    // STEP 2: Then extract thinking content from cleaned text
                    const { displayText, thinking } = this.parseThinkingContent(cleanedText);

                    // Send updated message to React component for real-time streaming
                    if (this.onMessageCallback) {
                        const chatMessage: ChatMessage = {
                            id: messageId,
                            text: displayText,
                            thinking: thinking,
                            sender: "ai",
                            timestamp: new Date(),
                        };
                        console.log("📨 Streaming AI message update:", chatMessage);
                        this.onMessageCallback(chatMessage);
                    }
                } else if (data.event === "done") {
                    // Finalize message and clean up
                    console.log("📨 AI response completed, messageId:", messageId);
                    this.currentMessages.delete(messageId);
                    // No need to send final message - already sent during delta events
                }
            } else {
                console.log("📨 Unhandled message format:", data);
            }
        } catch (error) {
            console.error("❌ Error parsing WebSocket message:", error);
            console.error("Raw data:", event.data);
        }
    };

    private attemptReconnect(): void {
        this.reconnectAttempts++;
        console.log(`
            🔄 Attempting reconnection (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

        setTimeout(() => {
            this.connect().catch(() => {
                // Reconnection failed, will try again if attempts remain
            });
        }, this.reconnectDelay * this.reconnectAttempts);
    }

    updateJwtToken(jwtToken: string): void {
        console.log("🔑 Updating JWT token");
        this.jwtToken = jwtToken;
    }

    sendMessage(message: string, userId: string): void {
        if (this.ws?.readyState === WebSocket.OPEN) {
            const payload = {
                action: "converse",
                user_id: userId,
                data: message,
                token: this.jwtToken,
            };

            console.log("📤 Sending WebSocket message with token:", {
                ...payload,
                token: this.jwtToken ? "Present" : "Missing",
            });
            this.ws.send(JSON.stringify(payload));
        } else {
            console.error("❌ Cannot send message - WebSocket not connected");
        }
    }

    disconnect(): void {
        if (this.ws) {
            console.log("🔌 Disconnecting WebSocket");
            this.ws.close(1000, "User closed chat");
            this.ws = null;
        }
    }

    isConnected(): boolean {
        return this.ws?.readyState === WebSocket.OPEN;
    }

    onMessage(callback: (message: ChatMessage) => void): void {
        this.onMessageCallback = callback;
    }

    onConnect(callback: () => void): void {
        this.onConnectCallback = callback;
    }

    onDisconnect(callback: () => void): void {
        this.onDisconnectCallback = callback;
    }

    onError(callback: (error: Event) => void): void {
        this.onErrorCallback = callback;
    }
}
